import { getDomain } from '@/common/api';
import { getPageProps, isIOS, localStorage } from '@kibt/krn-utils';
import queryString from 'query-string';
import { openUrl } from '@/common/bridge/openUrl';
import { NativeModules } from 'react-native';
import { isWeb } from '@/common/device-info';
import { delay } from '@/utils/delay';
import yoda from '@kibt/bridge';

import { k18nPro } from './k18npro';
import { $k18n } from '@/common/i18n';
import { useQuery, useQuerys } from '@/hooks/useQuery';
import { reportCatchError } from '@/utils/reportCatchError';
import { radar } from '@/common/log';

// true 代表 query 为空
export const checkIsQueryEmpty = (query: string) => {
    if (!query || query === 'undefined' || query === 'null') {
        return true;
    }
    return false;
};

export const getCombosTip = rule => {
    return $k18n({
        id: 'k_447737',
        text: '{X}件商品享受{Y}% OFF',
        params: {
            X: rule?.[0],
            Y: rule?.[1],
        },
    });
};

// 获取从商详页传入的retrievalInfo用于后续监控
export const useRetrievalInfo = () => {
    const ret = useQuery<string>('retrievalInfo');
    try {
        if (checkIsQueryEmpty(ret)) {
            return;
        }
        return JSON.parse(decodeURIComponent(ret || '{}'));
    } catch (e) {
        reportCatchError(e, {
            position: 'useRetrievalInfo',
            data: ret,
        });
        return;
    }
};

export const getTakeTogetherTip = rule => {
    return $k18n({
        id: 'k_445183',
        text: '满{X}件享{Y}%OFF',
        params: {
            X: rule?.[0],
            Y: rule?.[1],
        },
    });
};

export const getPureDiscountTip = ({ tip, placeHolder }) => {
    const text = k18nPro({
        text: tip,
        params: placeHolder,
    });
    return text;
};

// 触发rn的全局事件，重置商详页的商品购买数量
export const dispatchSetDetailBuyNumEvent = () => {
    if (yoda?.event) {
        yoda.event.dispatchGlobalEvent({
            type: 'ecommerce://onSetDetailBuyNum',
        });
    }
};

export function useExtParams() {
    const jsonStr = useQuery<string>('extParams') || '{}';
    try {
        return JSON.parse(decodeURIComponent(jsonStr));
    } catch (e) {
        reportCatchError(e, {
            position: 'useExtParams',
            data: jsonStr,
        });
        return {};
    }
}

export interface IOrderParam {
    cartItemId?: string | number; // 购物车来的需要
    itemId: string | number;
    merchantId: string | number;
    skuId: string | number;
    sellerMerchantId?: string | number;
    mediaSource?: number;
    mediaDetailSource?: string;
    num: string;
    flashSaleActivityId?: string | number; // 秒杀活动sku维度的id，不参与活动=0，参与活动>0
    marketingType?: string | number; // 活动类型
    marketingId?: string | number; // 活动id
    distributorDiscount?: {
        promotionId?: string | number;
    };
    clickid?: string;
}

export function useOrderParamsFromQuery() {
    const { productItems, fromMyShoppingCar, itemId, merchantId, skuId, sellerMerchantId, buyamount, clickid } = useQuerys();

    async function getOrderParamsFromQuery(): Promise<IOrderParam[]> {
        const checkParams = async () => {
            radar.event({
                name: 'AfterInitOrderPageV2',
                params: {
                    schema: getPageProps(),
                    productItems,
                    finallyProductItems: JSON.parse(decodeURIComponent(productItems || '[]')),
                    storage: await localStorage.getItem('MyShoppingCarParams'),
                },
            });
        };
        checkParams();
        try {
            if (fromMyShoppingCar) {
                const localStr = (await localStorage.getItem('MyShoppingCarParams')) as string;
                if (localStr) {
                    return JSON.parse(localStr).productItems;
                }
            }
            if (productItems) {
                const list = JSON.parse(decodeURIComponent(productItems || '[]'));
                return list;
            }
            return [
                {
                    itemId,
                    merchantId,
                    skuId,
                    clickid,
                    sellerMerchantId,
                    num: buyamount,
                },
            ];
        } catch (error) {
            reportCatchError(error, {
                position: 'getOrderParamsFromQuery',
            });
            return [];
        }
    }

    return { getOrderParamsFromQuery };
}

// 关于刚进确认订单页 如果商品详情创建了临时地址的逻辑
export const gotoAddAddressWithTemp = async () => {
    const _url = `${getDomain()}/shop/c/address/add?referer_page=krnOrderConfirm_4&pageorigin=confirmOrder&entertSource=2&formKrn=1&hyId=kibt-fe-shop-c-v1&__launch_options__=%7B%22topBarPosition%22%3A%22fixed%22%7D`;
    const unParseTemporaryAddressInLocal: any = await localStorage.getItem('detail-page-temporaryAddress');
    const temporaryAddressInLocal: any = unParseTemporaryAddressInLocal ? JSON.parse(unParseTemporaryAddressInLocal) : null;
    let postalCode, province, city, district, street, country;
    if (temporaryAddressInLocal) {
        postalCode = temporaryAddressInLocal['detail-page-address-postalCode'] || '';
        province = temporaryAddressInLocal['detail-page-address-province'] || '';
        city = temporaryAddressInLocal['detail-page-address-city'] || '';
        district = temporaryAddressInLocal['detail-page-address-district'] || '';
        street = temporaryAddressInLocal['detail-page-address-street'] || '';
    }
    const url = queryString.stringifyUrl(
        {
            url: _url,
            query: {
                'krn-address-postalCode': postalCode as string,
                'krn-address-province': province as string,
                'krn-address-city': city as string,
                'krn-address-district': district as string,
                'krn-address-street': street as string,
            },
        },
        { skipNull: true, sort: false }
    );

    // iosrn tip 需要等Model先关闭 不关闭的话，无法实现跳走
    if (isIOS && !isWeb) {
        await delay(1500); // 先增加到100ms，兼容部分低端机型关闭弹窗慢的case
    }

    openUrl({ url });
};

export const removeLocalAddress = () => {
    localStorage.removeItem('detail-page-temporaryAddress');
};

// iosrn tip RootTagMap 是为了解决提单页到订单详情页反复跳的问题@taofeng
export async function closeCurPage() {
    try {
        NativeModules?.KRNBasic?.exitPageWithInfo?.({
            viewTag: getPageProps?.()?.rootTag,
            // ios下animate必须为0: https://kds.corp.kuaishou.com/kds-react/document/practice-new/07-%E6%89%93%E5%BC%80%E6%96%B0%E9%A1%B5%E9%9D%A2%E5%90%8C%E6%97%B6%E5%85%B3%E9%97%AD%E6%97%A7%E9%A1%B5%E9%9D%A2.html
            animate: isIOS ? 0 : 1,
        });
        return Promise.resolve({ result: 1 });
    } catch (error) {
        reportCatchError(error, {
            position: 'closeCurPage',
        });
        return Promise.reject(error);
    }
}
